import { useEffect, useState } from 'react';
import {
  TrendingUp,
  Clock,
  Bot,
  CheckCircle,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { adminApi, ConversationMetric } from '../lib/admin-api';
import { StatsCard } from '../components/admin/StatsCard';

type PeriodType = 'day' | 'week' | 'month';

export function AnalyticsPage() {
  const { profile } = useAuth();
  const [metrics, setMetrics] = useState<ConversationMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState<PeriodType>('day');

  useEffect(() => {
    async function fetchData() {
      if (!profile?.organization_id) return;

      setLoading(true);
      const data = await adminApi.getAnalytics(
        profile.organization_id,
        periodType
      );
      setMetrics(data);
      setLoading(false);
    }

    fetchData();
  }, [profile?.organization_id, periodType]);

  // Calculate summary stats
  const summary = metrics.reduce(
    (acc, m) => ({
      totalConversations: acc.totalConversations + m.total_conversations,
      resolved: acc.resolved + m.resolved_conversations,
      aiResolved: acc.aiResolved + m.ai_resolved,
      humanResolved: acc.humanResolved + m.human_resolved,
      avgResponseTime: m.avg_first_response_seconds || acc.avgResponseTime,
      avgCsat: m.avg_csat_score || acc.avgCsat,
    }),
    {
      totalConversations: 0,
      resolved: 0,
      aiResolved: 0,
      humanResolved: 0,
      avgResponseTime: 0,
      avgCsat: 0,
    }
  );

  const containmentRate = summary.resolved > 0
    ? Math.round((summary.aiResolved / summary.resolved) * 100)
    : 0;

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="support-page">
      <div className="support-page-heading">
        <div>
          <h1 className="support-page-title">Analytics</h1>
          <p className="text-muted-foreground mt-1">Performance metrics and insights</p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Period:</span>
          <select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value as PeriodType)}
            className="border border-border rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-ring focus:border-primary"
          >
            <option value="day">Daily</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Conversations"
          value={summary.totalConversations}
          icon={TrendingUp}
          tone="primary"
        />
        <StatsCard
          title="Resolved"
          value={summary.resolved}
          icon={CheckCircle}
          tone="success"
        />
        <StatsCard
          title="AI Containment Rate"
          value={`${containmentRate}%`}
          icon={Bot}
          tone="info"
        />
        <StatsCard
          title="Avg First Response"
          value={formatDuration(summary.avgResponseTime)}
          icon={Clock}
          tone="warning"
        />
      </div>

      {/* Resolution Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* AI vs Human */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4">Resolution Breakdown</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">AI Resolved</span>
                <span className="font-medium">{summary.aiResolved}</span>
              </div>
              <div className="w-full bg-border rounded-full h-2">
                <div
                  className="bg-info h-2 rounded-full"
                  style={{
                    width: `${
                      summary.resolved > 0
                        ? (summary.aiResolved / summary.resolved) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Human Resolved</span>
                <span className="font-medium">{summary.humanResolved}</span>
              </div>
              <div className="w-full bg-border rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full"
                  style={{
                    width: `${
                      summary.resolved > 0
                        ? (summary.humanResolved / summary.resolved) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* CSAT */}
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4">Customer Satisfaction</h3>
          <div className="flex items-center justify-center h-32">
            {summary.avgCsat > 0 ? (
              <div className="text-center">
                <div className="text-5xl font-bold text-link">
                  {summary.avgCsat.toFixed(1)}
                </div>
                <div className="text-sm text-muted-foreground mt-2">out of 5.0</div>
                <div className="flex justify-center mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`text-2xl ${
                        star <= Math.round(summary.avgCsat)
                          ? 'text-warning-foreground'
                          : 'text-muted-foreground'
                      }`}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">No CSAT data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Metrics Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-semibold text-foreground">Period Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  New
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  Resolved
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  AI
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                  Avg Response
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {metrics.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                    No metrics data available
                  </td>
                </tr>
              ) : (
                metrics.map((m) => (
                  <tr key={m.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                      {new Date(m.period_start).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {m.total_conversations}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {m.new_conversations}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {m.resolved_conversations}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-info-foreground">
                      {m.ai_resolved}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {formatDuration(m.avg_first_response_seconds)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
